/**
 * Utilities for Google Sheets rich-text metadata.
 *
 * Expected backend format (descriptionRich):
 * [
 *   { start: 0, end: 12, text: "Testo ", bold: false, italic: false },
 *   { start: 12, end: 20, text: "evidenza", bold: true, italic: false }
 * ]
 *
 * start/end are offsets in the original plain-text cell value. For backwards
 * compatibility, arrays containing only { text, ...styles } are also accepted.
 */

const asBool = (value) => value === true;

export const normalizeRichRuns = (text, input) => {
  const source = String(text || "").replace(/\r\n?/g, "\n");
  if (!Array.isArray(input) || !input.length || !source) return [];

  let cursor = 0;
  const out = [];

  input.forEach((raw) => {
    if (!raw || typeof raw !== "object") return;

    let start = Number.isFinite(Number(raw.start)) ? Number(raw.start) : null;
    let end = Number.isFinite(Number(raw.end)) ? Number(raw.end) : null;
    const runText = raw.text == null ? "" : String(raw.text).replace(/\r\n?/g, "\n");

    if (start == null) start = cursor;
    if (end == null) end = start + runText.length;

    start = Math.max(0, Math.min(source.length, start));
    end = Math.max(start, Math.min(source.length, end));
    cursor = end;

    if (end <= start) return;

    out.push({
      start,
      end,
      bold: asBool(raw.bold),
      italic: asBool(raw.italic),
      underline: asBool(raw.underline),
      strikethrough: asBool(raw.strikethrough || raw.strike),
    });
  });

  return out.sort((a, b) => a.start - b.start || a.end - b.end);
};

export const richRunsForSlice = (fullText, input, start = 0, end = null) => {
  const source = String(fullText || "").replace(/\r\n?/g, "\n");
  const sliceStart = Math.max(0, Number(start) || 0);
  const sliceEnd = end == null ? source.length : Math.max(sliceStart, Math.min(source.length, Number(end) || 0));
  const runs = normalizeRichRuns(source, input);

  return runs
    .map((run) => ({
      ...run,
      start: Math.max(run.start, sliceStart),
      end: Math.min(run.end, sliceEnd),
    }))
    .filter((run) => run.end > run.start);
};

export const hasRichStyles = (runs) =>
  Array.isArray(runs) &&
  runs.some((r) => r && (r.bold || r.italic || r.underline || r.strikethrough || r.strike));

/**
 * Applies a slice of rich text to an existing DOM element. Used by the hidden
 * pagination measuring layer, so bold/italic wrapping is measured exactly as
 * it will appear in the PDF.
 */
export const applyRichTextToElement = (
  el,
  fullText,
  input,
  start = 0,
  end = null,
) => {
  if (!el) return;

  const source = String(fullText || "").replace(/\r\n?/g, "\n");
  const sliceStart = Math.max(0, Number(start) || 0);
  const sliceEnd = end == null ? source.length : Math.max(sliceStart, Math.min(source.length, Number(end) || 0));
  const runs = richRunsForSlice(source, input, sliceStart, sliceEnd);

  el.replaceChildren();
  let cursor = sliceStart;

  const appendText = (from, to, style = null) => {
    if (to <= from) return;
    const value = source.slice(from, to);
    if (!style || (!style.bold && !style.italic && !style.underline && !style.strikethrough)) {
      el.appendChild(document.createTextNode(value));
      return;
    }
    const span = document.createElement("span");
    if (style.bold) span.style.fontWeight = "700";
    if (style.italic) span.style.fontStyle = "italic";
    const decorations = [];
    if (style.underline) decorations.push("underline");
    if (style.strikethrough) decorations.push("line-through");
    if (decorations.length) span.style.textDecoration = decorations.join(" ");
    span.textContent = value;
    el.appendChild(span);
  };

  runs.forEach((run) => {
    if (run.start > cursor) appendText(cursor, run.start);
    appendText(Math.max(cursor, run.start), run.end, run);
    cursor = Math.max(cursor, run.end);
  });

  if (cursor < sliceEnd) appendText(cursor, sliceEnd);
};
